import React, { useEffect, useCallback, useState } from 'react';
import moment from 'moment';
import { DailySummaryDto } from '@peartherapeutics/hylia-api-client';
import { shiftDate } from 'utils/dateTime';
import Header from './Header';
import { draw, transformToChartData } from './chart';
import DateRangeSelector from '../../../../common/DateRangeSelector';
import styles from './styles.module.scss';

export interface IIntensityChartProps {
  minDate: string;
  maxDate: string;
  timezone: string;
  dailies?: DailySummaryDto[];
  onDateRangeChanged: (to: string, from: string) => void;
}

const IntensityChart = (props: IIntensityChartProps) => {
  const { minDate, maxDate, dailies, timezone, onDateRangeChanged } = props;
  const startDateDefault = shiftDate(new Date(minDate), timezone).toISOString();
  const shiftedEndDate = shiftDate(new Date(maxDate), timezone).toISOString();
  const shiftedNow = shiftDate(new Date(), timezone).toISOString();
  const endDateDefault =
    moment(maxDate).tz(timezone).diff(moment().tz(timezone), 'day') > 0
      ? shiftedNow
      : shiftedEndDate;
  const [startDate, setStartDate] = useState<string>(startDateDefault);
  const [endDate, setEndDate] = useState<string>(endDateDefault);
  const ELEMENT_ID = 'intensity-chart';

  const handleChangeDate = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  const drawChart = useCallback(
    (props: { timezone: string; dailies: DailySummaryDto[] }): void => {
      const { dailies, timezone } = props;
      const subset = dailies.filter((d) =>
        moment(d.date)
          .tz(timezone)
          .isBetween(
            moment(startDate).startOf('day'),
            moment(endDate).endOf('day'),
            undefined,
            '[]',
          ),
      );
      const chartData = transformToChartData(subset);
      draw({
        elementId: ELEMENT_ID,
        chartData,
        timezone,
        maxDate: endDate,
      });
      onDateRangeChanged(startDate, endDate);
    },
    [startDate, endDate, onDateRangeChanged],
  );

  useEffect(() => {
    if (dailies) drawChart({ timezone, dailies });
  }, [startDate, endDate, timezone, dailies, drawChart]);

  return (
    <div className={styles.container} data-testid="intensity-chart">
      <Header />
      <div className={styles['date-wrapper']}>
        <DateRangeSelector
          onChange={handleChangeDate}
          minDate={startDateDefault}
          maxDate={endDateDefault}
          startDate={startDate}
          endDate={endDate}
          defaultRange={moment(maxDate).isBefore(moment()) ? 'all' : '1m'}
        />
      </div>
      <div id={ELEMENT_ID} />
    </div>
  );
};

export default IntensityChart;
